/* eslint-disable react/prop-types */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Building2, CheckCircle2, ExternalLink, Pencil, Upload } from "lucide-react";
import SearchPicker from "../../../../components/admin/SearchPicker.jsx";
import { SelectDropdown, StatusBadge, TextInput, showAlert, showErrorAlert } from "../../../../components/ui/index.js";
import getMediaUrl from "../../../../components/tools/utils.jsx";
import { useAuthContext } from "../../../../components/providers/AuthProvider.jsx";
import { canManageAllProjects } from "../../../../lib/permissions.js";
import { CUSTOMER_STATE } from "../../../../components/catalog/catalogUtils.js";
import {
    BackButton, ContactActions, CrmAvatar, CrmDetailSkeleton, CrmField, CrmInput, CrmSection, EditBar,
} from "../../../../components/crm/CrmParts.jsx";
import CrmActivityPanel from "../../../../components/crm/CrmActivityPanel.jsx";
import {
    LEAD_SOURCES, LEAD_STAGES, apiErrorText, clienteName, formatDate, labelFor, leadName, optionFor, userLabel,
} from "../../../../components/crm/crmUtils.js";

const TEXT_FIELDS = ["company_name", "contact_name", "email", "phone", "website", "address", "channel", "notes", "internal_notes"];

const EMPTY = {
    stage: "prospecto", source: "manual", channel: "", company_name: "", contact_name: "", email: "", phone: "",
    website: "", address: "", notes: "", internal_notes: "",
    user: null, user_label: "", cliente: null, cliente_label: "",
    customized_keyring: null, customized_keyring_label: "", custom_order_request: null, custom_order_request_label: "",
    assigned_to: null, assigned_to_label: "",
};

const draftFrom = (lead) => ({
    ...Object.fromEntries(TEXT_FIELDS.map((field) => [field, lead[field] || ""])),
    stage: lead.stage,
    source: lead.source,
    user: lead.user || null,
    user_label: lead.user_email || "",
    cliente: lead.cliente || null,
    cliente_label: lead.cliente_name || "",
    customized_keyring: lead.customized_keyring || null,
    customized_keyring_label: lead.customized_keyring_name || "",
    custom_order_request: lead.custom_order_request || null,
    custom_order_request_label: lead.custom_order_request_label || "",
    assigned_to: lead.assigned_to || null,
    assigned_to_label: lead.assigned_to_name || "",
});

/**
 * Ficha de un lead en una sola página (C3D-46). Sin `leadId` es el alta.
 * La etapa se cambia al momento desde la cabecera; el resto, con Editar.
 */
export default function LeadFicha({ leadId, onBack, onSaved }) {
    const navigate = useNavigate();
    const authContext = useAuthContext();
    const seesAll = canManageAllProjects(authContext);
    const isAdmin = authContext.role === "admin";
    const isNew = !leadId;

    const [lead, setLead] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [editing, setEditing] = useState(isNew);
    const [draft, setDraft] = useState(EMPTY);
    const [logoFile, setLogoFile] = useState(null);
    const [errors, setErrors] = useState(null);
    const [saving, setSaving] = useState(false);
    const [busy, setBusy] = useState(false);
    const [activityKey, setActivityKey] = useState(0);

    const load = useCallback(() => {
        if (isNew) return Promise.resolve();
        setLoadError(null);
        return axios.get(`/leads/${leadId}/`)
            .then(({ data }) => { setLead(data); setDraft(draftFrom(data)); })
            .catch((error) => setLoadError(error?.response?.status === 404 ? "not_found" : "network"));
    }, [leadId, isNew]);
    useEffect(() => { load(); }, [load]);

    if (loadError) {
        return (
            <div className="p-4 flex flex-col items-start gap-3">
                <p role="alert" className="text-error">{loadError === "not_found" ? "Este lead no existe o no lo llevas tú." : "No se ha podido cargar el lead."}</p>
                <div className="flex gap-2">
                    <button type="button" className="btn btn-sm" onClick={onBack}>Volver a leads</button>
                    {loadError !== "not_found" && <button type="button" className="btn btn-sm btn-primary" onClick={load}>Reintentar</button>}
                </div>
            </div>
        );
    }
    if (!isNew && !lead) return <CrmDetailSkeleton />;

    const set = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));
    const original = isNew ? EMPTY : draftFrom(lead);
    const dirty = Boolean(logoFile) || JSON.stringify(draft) !== JSON.stringify(original);
    const fieldError = (field) => errors?.[field];

    const save = async () => {
        setSaving(true);
        setErrors(null);
        const payload = {
            ...Object.fromEntries(TEXT_FIELDS.map((field) => [field, draft[field]])),
            stage: draft.stage,
            source: draft.source,
            user: draft.user || null,
            cliente: draft.cliente || null,
            customized_keyring: draft.customized_keyring || null,
            custom_order_request: draft.custom_order_request || null,
            ...(seesAll ? { assigned_to: draft.assigned_to || null } : {}),
        };
        try {
            let { data } = isNew ? await axios.post("/leads/", payload) : await axios.patch(`/leads/${leadId}/`, payload);
            if (logoFile) {
                const form = new FormData();
                form.append("logo_asset", logoFile);
                ({ data } = await axios.patch(`/leads/${data.id}/`, form, { headers: { "Content-Type": "multipart/form-data" } }));
            }
            setLogoFile(null);
            if (isNew) {
                onSaved(data.id);
                return;
            }
            setLead(data);
            setDraft(draftFrom(data));
            setEditing(false);
            setActivityKey((key) => key + 1);
        } catch (error) {
            const data = error?.response?.data;
            setErrors(data && typeof data === "object" ? data : { detail: apiErrorText(error, "No se ha podido guardar.") });
        } finally {
            setSaving(false);
        }
    };

    const cancel = () => {
        if (isNew) { onBack(); return; }
        setDraft(draftFrom(lead));
        setLogoFile(null);
        setErrors(null);
        setEditing(false);
    };

    const changeStage = async (stage) => {
        if (stage === lead.stage) return;
        setBusy(true);
        try {
            const { data } = await axios.patch(`/leads/${leadId}/`, { stage });
            setLead(data);
            setDraft(draftFrom(data));
        } catch (error) {
            showErrorAlert({ text: apiErrorText(error, "No se ha podido cambiar la etapa.") });
        } finally {
            setBusy(false);
        }
    };

    const convert = async () => {
        setBusy(true);
        try {
            const { data } = await axios.post(`/leads/${leadId}/convert/`, lead.cliente ? { cliente_id: lead.cliente } : {});
            setLead(data);
            setDraft(draftFrom(data));
            setActivityKey((key) => key + 1);
            showAlert({ title: "Convertido en cliente", text: `${leadName(data)} ya es cliente. Sus proyectos pasan también a su ficha.`, icon: "success" });
        } catch (error) {
            showErrorAlert({ text: apiErrorText(error, "No se ha podido convertir el lead.") });
        } finally {
            setBusy(false);
        }
    };

    const name = isNew ? (leadName(draft) || "Nuevo lead") : leadName(lead);
    const stage = optionFor(LEAD_STAGES, lead?.stage);
    const state = lead ? CUSTOMER_STATE[lead.customer_state] : null;
    const generalError = errors && (errors.detail || errors.non_field_errors);
    const newProjectParam = lead ? `lead:${lead.id}&nombre=${encodeURIComponent(name)}` : "";

    return (
        <div className="p-2 sm:p-4 flex flex-col gap-4">
            <BackButton label="Leads" onClick={onBack} />

            <header className="rounded-xl border border-base-content/10 bg-base-100 p-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-4 min-w-0">
                    <CrmAvatar name={name} imageUrl={lead?.logo_asset} size="lg" />
                    <div className="flex flex-col gap-1.5 min-w-0">
                        <h1 className="text-2xl font-bold leading-tight truncate">{name}</h1>
                        {!isNew && (
                            <div className="flex flex-wrap items-center gap-1.5">
                                {stage && <StatusBadge label={stage.label} tone={stage.tone} size="sm" />}
                                {state && lead.customer_state !== "lead" && <StatusBadge label={lead.customer_state === "cliente" ? "Ha comprado" : state.label} tone={state.tone} title={state.title} size="sm" outline />}
                                <span className="text-xs text-base-content/60">
                                    {labelFor(LEAD_SOURCES, lead.source)}{lead.channel ? ` · ${lead.channel}` : ""}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
                {!isNew && !editing && (
                    <div className="flex flex-wrap gap-2 md:justify-end">
                        <ContactActions phone={lead.phone} email={lead.email} website={lead.website} />
                        <button type="button" className="btn btn-outline btn-sm gap-1.5" onClick={() => setEditing(true)}>
                            <Pencil className="w-4 h-4" aria-hidden="true" /> Editar
                        </button>
                    </div>
                )}
            </header>

            {!isNew && !editing && (
                <div className="rounded-xl border border-base-content/10 bg-base-100 p-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Etapa</span>
                        <SelectDropdown options={LEAD_STAGES} value={lead.stage} onChange={changeStage} size="sm" className="w-44" disabled={busy} />
                    </div>
                    {lead.cliente ? (
                        <button type="button" className="btn btn-ghost btn-sm gap-1.5" onClick={() => navigate(`/admin/clientes?id=${lead.cliente}`)}>
                            <Building2 className="w-4 h-4" aria-hidden="true" /> Ver ficha de cliente{lead.cliente_name ? `: ${lead.cliente_name}` : ""}
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="btn btn-success btn-sm gap-1.5"
                            onClick={convert}
                            disabled={busy || !lead.email}
                            title={lead.email ? "Crear su ficha de cliente" : "Hace falta un email para crear el cliente"}
                        >
                            <CheckCircle2 className="w-4 h-4" aria-hidden="true" /> Convertir en cliente
                        </button>
                    )}
                </div>
            )}

            <div className={`grid gap-4 items-start ${isNew ? "" : "lg:grid-cols-[1fr_22rem]"}`}>
                <div className="flex flex-col gap-4 min-w-0">
                    {generalError && <div role="alert" className="alert alert-error text-sm">{String(generalError)}</div>}

                    <CrmSection title="Contacto">
                        {editing ? (
                            <div className="grid gap-3 md:grid-cols-2">
                                <CrmInput label="Empresa" error={fieldError("company_name")}>
                                    <TextInput value={draft.company_name} onChange={(e) => set("company_name", e.target.value)} autoFocus={isNew} />
                                </CrmInput>
                                <CrmInput label="Persona de contacto" error={fieldError("contact_name")}>
                                    <TextInput value={draft.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
                                </CrmInput>
                                <CrmInput label="Email" error={fieldError("email")}>
                                    <TextInput type="email" value={draft.email} onChange={(e) => set("email", e.target.value)} />
                                </CrmInput>
                                <CrmInput label="Teléfono" error={fieldError("phone")}>
                                    <TextInput value={draft.phone} onChange={(e) => set("phone", e.target.value)} placeholder="600 000 000" />
                                </CrmInput>
                                <CrmInput label="Web" error={fieldError("website")} help="Vale sin https://">
                                    <TextInput value={draft.website} onChange={(e) => set("website", e.target.value)} placeholder="www.ejemplo.com" />
                                </CrmInput>
                                <CrmInput label="Dirección" error={fieldError("address")}>
                                    <TextInput value={draft.address} onChange={(e) => set("address", e.target.value)} />
                                </CrmInput>
                                <p className="md:col-span-2 text-xs text-base-content/60">Hace falta al menos la empresa, el contacto, el email o el teléfono.</p>
                            </div>
                        ) : (
                            <dl className="grid gap-4 sm:grid-cols-2">
                                <CrmField label="Empresa">{lead.company_name}</CrmField>
                                <CrmField label="Persona de contacto">{lead.contact_name}</CrmField>
                                <CrmField label="Email">{lead.email}</CrmField>
                                <CrmField label="Teléfono">{lead.phone}</CrmField>
                                <CrmField label="Web">{lead.website && <a className="link link-hover" href={lead.website} target="_blank" rel="noopener noreferrer">{lead.website.replace(/^https?:\/\//, "")}</a>}</CrmField>
                                <CrmField label="Dirección">{lead.address}</CrmField>
                            </dl>
                        )}
                    </CrmSection>

                    <CrmSection title="Origen y logo">
                        {editing ? (
                            <div className="grid gap-3 md:grid-cols-2">
                                <div className="form-control gap-1">
                                    <span className="label-text text-sm font-medium">Etapa</span>
                                    <SelectDropdown options={LEAD_STAGES} value={draft.stage} onChange={(value) => set("stage", value)} className="w-full" />
                                </div>
                                <div className="form-control gap-1">
                                    <span className="label-text text-sm font-medium">Fuente</span>
                                    <SelectDropdown options={LEAD_SOURCES} value={draft.source} onChange={(value) => set("source", value)} className="w-full" />
                                </div>
                                <CrmInput label="Canal o detalle" error={fieldError("channel")} help="Por ejemplo: «Google Maps, bares de Triana»">
                                    <TextInput value={draft.channel} onChange={(e) => set("channel", e.target.value)} />
                                </CrmInput>
                                <CrmInput label="Logo" error={fieldError("logo_asset")} help={logoFile ? logoFile.name : "PNG, JPEG o WebP"}>
                                    <input
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        className="file-input file-input-bordered file-input-sm w-full"
                                        onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                                    />
                                </CrmInput>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-4 sm:flex-row">
                                {lead.logo_asset && (
                                    <a href={getMediaUrl(lead.logo_asset)} target="_blank" rel="noopener noreferrer" className="shrink-0 rounded-lg border border-base-content/10 bg-base-200 p-2" title="Abrir el logo">
                                        <img src={getMediaUrl(lead.logo_asset)} alt={`Logo de ${name}`} className="h-24 w-24 object-contain" />
                                    </a>
                                )}
                                <dl className="grid gap-4 sm:grid-cols-2 flex-1">
                                    <CrmField label="Fuente">{labelFor(LEAD_SOURCES, lead.source)}</CrmField>
                                    <CrmField label="Canal o detalle">{lead.channel}</CrmField>
                                    <CrmField label="Alta">{formatDate(lead.created_at)}</CrmField>
                                    <CrmField label="Procedencia del logo">
                                        {lead.logo_source_url && (
                                            <a className="link link-hover inline-flex items-center gap-1" href={lead.logo_source_url} target="_blank" rel="noopener noreferrer">
                                                Ver fuente <ExternalLink className="w-3 h-3" aria-hidden="true" />
                                            </a>
                                        )}
                                    </CrmField>
                                </dl>
                            </div>
                        )}
                    </CrmSection>

                    <CrmSection title="Notas">
                        {editing ? (
                            <div className="grid gap-3 md:grid-cols-2">
                                <CrmInput label="Notas comerciales" error={fieldError("notes")}>
                                    <textarea className="textarea textarea-bordered w-full" rows={4} value={draft.notes} onChange={(e) => set("notes", e.target.value)} />
                                </CrmInput>
                                <CrmInput label="Notas internas" error={fieldError("internal_notes")} help="Solo las ve el equipo">
                                    <textarea className="textarea textarea-bordered w-full" rows={4} value={draft.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} />
                                </CrmInput>
                            </div>
                        ) : (
                            <dl className="grid gap-4 sm:grid-cols-2">
                                <CrmField label="Notas comerciales"><span className="whitespace-pre-line">{lead.notes}</span></CrmField>
                                <CrmField label="Notas internas"><span className="whitespace-pre-line">{lead.internal_notes}</span></CrmField>
                            </dl>
                        )}
                    </CrmSection>

                    <CrmSection title="Vínculos y gestión">
                        {editing ? (
                            <div className="grid gap-3 md:grid-cols-2">
                                <SearchPicker
                                    label="Cliente"
                                    value={draft.cliente}
                                    selectedLabel={draft.cliente_label}
                                    endpoint="/clients/"
                                    getOptionLabel={clienteName}
                                    getOptionDescription={(cliente) => cliente.email}
                                    onSelect={(option) => setDraft((prev) => ({ ...prev, cliente: option?.id || null, cliente_label: clienteName(option) }))}
                                />
                                <SearchPicker
                                    label="Usuario registrado"
                                    value={draft.user}
                                    selectedLabel={draft.user_label}
                                    endpoint="/projects/people-options/"
                                    paramsForQuery={(query) => ({ kind: "customer", search: query || undefined })}
                                    getOptionLabel={userLabel}
                                    onSelect={(option) => setDraft((prev) => ({ ...prev, user: option?.id || null, user_label: option?.email || "" }))}
                                />
                                <SearchPicker
                                    label="Diseño de referencia"
                                    value={draft.customized_keyring}
                                    selectedLabel={draft.customized_keyring_label}
                                    endpoint="/projects/design-options/"
                                    getOptionLabel={(design) => design?.name || ""}
                                    getOptionDescription={(design) => design.product_label}
                                    getOptionThumbnail={(design) => design.image_url}
                                    onSelect={(option) => setDraft((prev) => ({ ...prev, customized_keyring: option?.id || null, customized_keyring_label: option?.name || "" }))}
                                />
                                {isAdmin && (
                                    <SearchPicker
                                        label="Solicitud asociada"
                                        value={draft.custom_order_request}
                                        selectedLabel={draft.custom_order_request_label}
                                        endpoint="/custom-order-requests-admin/"
                                        placeholder="Buscar por modelo, email, empresa"
                                        getOptionLabel={(request) => `#${request.id} - ${request.customized_keyring_detail?.name || "Solicitud"}`}
                                        getOptionDescription={(request) => request.company_name || request.contact_email}
                                        onSelect={(option) => setDraft((prev) => ({ ...prev, custom_order_request: option?.id || null, custom_order_request_label: option ? `#${option.id} - ${option.customized_keyring_detail?.name || "Solicitud"}` : "" }))}
                                    />
                                )}
                                {seesAll && (
                                    <SearchPicker
                                        label="Comercial"
                                        value={draft.assigned_to}
                                        selectedLabel={draft.assigned_to_label}
                                        endpoint="/projects/people-options/"
                                        paramsForQuery={(query) => ({ kind: "sales", search: query || undefined })}
                                        getOptionLabel={userLabel}
                                        onSelect={(option) => setDraft((prev) => ({ ...prev, assigned_to: option?.id || null, assigned_to_label: userLabel(option) }))}
                                    />
                                )}
                            </div>
                        ) : (
                            <dl className="grid gap-4 sm:grid-cols-2">
                                <CrmField label="Cliente">{lead.cliente_name}</CrmField>
                                <CrmField label="Usuario registrado">{lead.user_email}</CrmField>
                                <CrmField label="Diseño de referencia">{lead.customized_keyring_name}</CrmField>
                                <CrmField label="Solicitud asociada">{lead.custom_order_request_label}</CrmField>
                                <CrmField label="Comercial">{lead.assigned_to_name}</CrmField>
                            </dl>
                        )}
                    </CrmSection>

                    {editing && (
                        <EditBar saving={saving} dirty={dirty} onCancel={cancel} onSave={save} saveLabel={isNew ? "Crear lead" : "Guardar"} />
                    )}
                    {editing && logoFile && (
                        <p className="text-xs text-base-content/60 flex items-center gap-1"><Upload className="w-3 h-3" aria-hidden="true" /> El logo se sube al guardar.</p>
                    )}
                </div>

                {!isNew && (
                    <aside className="flex flex-col gap-4 lg:sticky lg:top-2">
                        <CrmActivityPanel endpoint={`/leads/${lead.id}/`} newProjectParam={newProjectParam} reloadKey={activityKey} />
                    </aside>
                )}
            </div>
        </div>
    );
}
